import { useMemo, useState, useRef, useEffect } from 'react'

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import dayjs from 'dayjs'
import { T, VStack } from 'govwell-ui'
import { OrganizationHolidayDetailFragment } from 'types/graphql'

import { LoadingSpin } from 'src/components/shared/StyledComponents'
import { useOrganizationHolidaysQuery } from 'src/fetch/organizationHolidays'
import { DateFormats, formatSemanticDate } from 'src/utils/date'

const EARLIEST_YEAR = 2025
const LATEST_YEAR = 2030

type ScheduledHolidaysSectionProps = {
  orgSlug: string
}

const ScheduledHolidaysSection = ({ orgSlug }: ScheduledHolidaysSectionProps) => {
  const currentYear = dayjs().year()
  const [year, setYear] = useState(Math.max(currentYear, EARLIEST_YEAR))
  const hasLoadedAnyYear = useRef(false)
  const [cachedHolidays, setCachedHolidays] = useState<OrganizationHolidayDetailFragment[]>([])

  const { holidays, isLoading } = useOrganizationHolidaysQuery(orgSlug, year)

  // Track if we've ever successfully loaded data and cache the results
  useEffect(() => {
    if (!isLoading) {
      hasLoadedAnyYear.current = true
      setCachedHolidays(holidays)
    }
  }, [isLoading, holidays])

  // Use cached holidays when loading, current holidays when not loading
  const holidaysToShow = isLoading && hasLoadedAnyYear.current ? cachedHolidays : holidays

  const sortedHolidays = useMemo(
    () =>
      [...holidaysToShow].sort((a, b) =>
        dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : dayjs(a.date).isBefore(dayjs(b.date)) ? -1 : 0
      ),
    [holidaysToShow]
  )

  const handlePreviousYear = () => {
    setYear((prev) => (prev > EARLIEST_YEAR ? prev - 1 : prev))
  }

  const handleNextYear = () => {
    setYear((prev) => (prev < LATEST_YEAR ? prev + 1 : prev))
  }

  const shouldShowLoading = isLoading && !hasLoadedAnyYear.current

  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <VStack gap={4} align="start" width="100%">
        <div className="flex w-full items-center justify-between gap-3">
          <T.H3>Scheduled Holidays</T.H3>
          <div className="flex items-center gap-2">
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={handlePreviousYear}
              disabled={year <= EARLIEST_YEAR}
              aria-label="View previous year"
            />
            <T.H4 className="min-w-[4rem] text-center">{year}</T.H4>
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={faChevronRight} />}
              disabled={year >= LATEST_YEAR}
              onClick={handleNextYear}
              aria-label="View next year"
            />
          </div>
        </div>
        {shouldShowLoading ? (
          <LoadingSpin />
        ) : sortedHolidays.length === 0 ? (
          <T.Body>No scheduled holidays for {year}.</T.Body>
        ) : (
          <div className="grid w-full gap-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {sortedHolidays.map((holiday) => (
              <div
                key={holiday.id}
                className="flex flex-col gap-1 rounded-md border border-neutral-200 bg-neutral-50 p-4"
              >
                <T.H4>{holiday.name}</T.H4>
                <T.Body>
                  {formatSemanticDate(holiday.date, DateFormats.FullDateWithDayOfWeek)}
                </T.Body>
              </div>
            ))}
          </div>
        )}
      </VStack>
    </section>
  )
}

export default ScheduledHolidaysSection
